import * as Sentry from "@sentry/react";

const SENTRY_DSN =  "https://9486bcd10d1e175320aadf026952fb36@o4509149893623808.ingest.de.sentry.io/4509150012571728";
const RELEASE = process.env.REACT_APP_VERSION || 'unknown';

export const initSentry = () => {
  // console.log('🚀 Initializing Sentry with:', {
  //   dsn: SENTRY_DSN.substring(0, 50) + '...',
  //   release: RELEASE,
  //   debug: true
  // });

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: window.location.hostname === 'localhost' ? 'development' : 'production',
    release: RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Enable debug mode for better debugging
    debug: false,

    // Set high sample rate for all environments to ensure capture
    tracesSampleRate: 1.0,
    // tracePropagationTargets: [
    //   'localhost',
    //   /^http:\/\/localhost/,
    //   /^https:\/\/localhost/,
    //   'oogwai.gaggle.online',
    //   'analytics.oogw.ai'
    // ],

    // Enable session replay
    replaysSessionSampleRate: 0.5,
    replaysOnErrorSampleRate: 1.0,

    normalizeDepth: 10,

    maxBreadcrumbs: 50,

    attachStacktrace: true,


    beforeSend(event, hint) {
      // Always log in debug mode
      // console.log('Sentry Event:', event);
      // console.log('Sentry Hint:', hint);

      if (event.exception) {
        const error:any = hint.originalException;

        if (error && error.message) {
          if (error.message.includes('Network request failed') ||
              error.message.includes('Failed to fetch') ||
              error.message.includes('Load failed')) {
            event.fingerprint = ['network-error'];
          }

          if (error.message.includes('Non-Error promise rejection captured')) {
            return null;
          }

          if (error.message.includes('ResizeObserver loop limit exceeded')) {
            return null;
          }
        }
      }

      if (event.request?.url) {
        const sensitiveParams = ['token', 'password', 'secret', 'api_key', 'apiKey'];
        const url = new URL(event.request.url);

        sensitiveParams.forEach(param => {
          if (url.searchParams.has(param)) {
            url.searchParams.set(param, '[FILTERED]');
          }
        });

        event.request.url = url.toString();
      }

      return event;
    },

    beforeBreadcrumb(breadcrumb, hint) {
      // Allow all breadcrumbs
      // if (breadcrumb.category === 'console' && ENVIRONMENT === 'production') {
      //   return null;
      // }

      if (breadcrumb.type === 'http') {
        if (breadcrumb.data?.url?.includes('/health') ||
            breadcrumb.data?.url?.includes('/ping')) {
          return null;
        }
      }

      return breadcrumb;
    },

    // Minimal ignore list - only ignore truly noise errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],

    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],

    allowUrls: [
      // 'localhost',
      // /^http:\/\/localhost/,
      // /^https:\/\/localhost/,
      /https:\/\/oogwai\.gaggle\.online/,
      /https:\/\/analytics\.oogw\.ai/,
      /https:\/\/demo\.oogw\.ai/,
      /https:\/\/dev\.oogw\.ai/,
    ],
  });

  // Verify initialization - using getCurrentScope for newer Sentry versions
  try {
    const scope = Sentry.getCurrentScope();
    if (scope) {
      // console.log('✅ Sentry initialized successfully!');
      // console.log('DSN configured:', SENTRY_DSN ? 'Yes' : 'No');

      // Add Sentry to window for debugging
      (window as any).Sentry = Sentry;
      // console.log('🔧 Sentry added to window object for debugging');

      // Test by sending an init message
      // Sentry.captureMessage('Sentry initialized successfully', 'info');
    } else {
      // console.error('❌ Sentry scope not available!');
    }
  } catch (error) {
    // console.error('❌ Sentry initialization check failed:', error);
  }
};

export const setSentryUser = (user: {
  id?: string;
  email?: string;
  username?: string;
  workspace_id?: string;
  subscription_tier?: string;
}) => {
  if (user.id || user.email) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });

    if (user.workspace_id) {
      Sentry.setTag('workspace_id', user.workspace_id);
    }

    if (user.subscription_tier) {
      Sentry.setTag('subscription_tier', user.subscription_tier);
    }
  }
};

export const clearSentryUser = () => {
  Sentry.setUser(null);
};

export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};

export const captureException = (error: Error | unknown, context?: Record<string, any>) => {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext('additional', context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

export const addBreadcrumb = (breadcrumb: {
  message: string;
  category?: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, any>;
}) => {
  Sentry.addBreadcrumb({
    message: breadcrumb.message,
    category: breadcrumb.category || 'custom',
    level: breadcrumb.level || 'info',
    data: breadcrumb.data,
    timestamp: Date.now() / 1000,
  });
};

